import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Bell, ChevronDown, Info, MapPin, MoreVertical } from 'lucide-react'
import { cn } from '../utils/cn'

export interface YourCheckaNavigationProps {
  // Title section
  title?: string
  showTitle?: boolean

  // Info section
  infoText?: string
  infoHighlight?: string
  showInfo?: boolean

  // Location section
  locationLabel?: string
  locationValue?: string
  showLocation?: boolean
  onLocationClick?: () => void

  // Notification section
  notificationCount?: number
  showNotifications?: boolean
  onNotificationClick?: () => void

  // User section
  userName?: string
  userInitials?: string
  userAvatar?: string
  showUser?: boolean
  onUserMenuClick?: () => void

  // Menu section
  showMenu?: boolean
  onMenuClick?: () => void

  // Styling
  className?: string
  only?: boolean
}

export function YourCheckaNavigation({
  title = 'Assessment',
  showTitle = true,
  infoText = 'This assessment helps you discover your strengths and areas where you can grow. There are no right or wrong answers — ',
  infoHighlight = 'just be honest',
  showInfo = true,
  locationLabel = 'Current Location',
  locationValue = 'Ikeja, Lagos',
  showLocation = true,
  onLocationClick,
  notificationCount = 0,
  showNotifications = false,
  onNotificationClick,
  userName = 'John Doe',
  userInitials = 'JD',
  userAvatar,
  showUser = true,
  only = false,
  onUserMenuClick,
  showMenu = true,
  onMenuClick,
  className,
}: YourCheckaNavigationProps) {
  return (
    <nav
      style={{ justifyContent: only ? 'end' : 'space-between' }}
      className={cn(
        'flex items-center  gap-4 bg-white px-4 py-3 md:px-6 lg:px-8',
        className
      )}
      role='navigation'
      aria-label='Main navigation'
    >
      {/* Left Section - Title */}
      {showTitle && (
        <div className='flex items-center gap-3'>
          <h1 className='text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl'>
            {title}
          </h1>
        </div>
      )}

      {/* Center Section - Info Message */}
      {showInfo && (
        <div className='hidden flex-1 items-center gap-3 px-4 lg:flex'>
          <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500'>
            <Info className='h-4 w-4 text-white' aria-hidden='true' />
          </div>
          <p className='text-sm text-gray-600'>
            {infoText}
            <span className='font-semibold text-gray-900'>{infoHighlight}</span>
          </p>
        </div>
      )}

      {/* Right Section - Location, Notifications, User */}
      <div className='flex items-center gap-2 md:gap-3 lg:gap-4'>
        {/* Location Dropdown */}
        {showLocation && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='hidden gap-2 text-gray-700 hover:bg-gray-50 md:flex'
                onClick={onLocationClick}
                aria-label={`Current location: ${locationValue}`}
              >
                <MapPin
                  className='h-5 w-5 text-orange-500'
                  aria-hidden='true'
                />
                <div className='flex flex-col items-start'>
                  <span className='text-xs text-gray-500'>{locationLabel}</span>
                  <span className='text-sm font-medium text-gray-900'>
                    {locationValue}
                  </span>
                </div>
                <ChevronDown
                  className='h-4 w-4 text-gray-500'
                  aria-hidden='true'
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem>Change Location</DropdownMenuItem>
              <DropdownMenuItem>Location Settings</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Notifications */}
        {showNotifications && (
          <Button
            variant='ghost'
            size='icon'
            className='relative hover:bg-gray-50'
            onClick={onNotificationClick}
            aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
          >
            <Bell className='h-5 w-5 text-gray-700' aria-hidden='true' />
            {notificationCount > 0 && (
              <Badge
                className='absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-xs font-semibold text-white'
                aria-label={`${notificationCount} unread notifications`}
              >
                {notificationCount}
              </Badge>
            )}
          </Button>
        )}

        {/* User Menu */}
        {showUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='flex items-center gap-2 hover:bg-gray-50'
                onClick={onUserMenuClick}
                aria-label={`User menu for ${userName}`}
              >
                <Avatar className='h-10 w-10'>
                  <AvatarImage
                    src={userAvatar || '/placeholder.svg'}
                    alt={userName}
                  />
                  <AvatarFallback className='bg-gray-900 text-sm font-semibold text-white'>
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <span className='hidden text-sm font-medium text-gray-900 lg:inline'>
                  {userName}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem>Profile</DropdownMenuItem>
              <DropdownMenuItem>Settings</DropdownMenuItem>
              <DropdownMenuItem>Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* More Menu */}
        {showMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='hover:bg-gray-50'
                onClick={onMenuClick}
                aria-label='More options'
              >
                <MoreVertical
                  className='h-5 w-5 text-gray-700'
                  aria-hidden='true'
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem>Help</DropdownMenuItem>
              <DropdownMenuItem>Feedback</DropdownMenuItem>
              <DropdownMenuItem>About</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  )
}
